import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";
import { Constants, CKC_TCN_FEE_LABEL } from "@eagerdog/constants";

import { IShow, ITransactionShowDetails, ITransactionDogDetails, ITransactionElementDetails, ITransactionPaymentSummary } from "@eagerdog/interfaces";
import { IShowDogs } from "../../routes/EventRegister/EventRegister";
import { helperService } from "src/services/helper.service";

import Input from "../Input/Input";
import Button from "../Button/Button";
import PointRedemption from "../PointRedemption/PointRedemption";
import CartTotals, { ICartTotalLine } from "../CartTotals/CartTotals";

import "./Cart.scss";

interface IProps {
  onApplyDiscount?(discount: string): void,
  onRedeemPoints?(points: number): void,
  eventId?: string,
  clubId?: string,
  paymentSummary: ITransactionPaymentSummary,
  previewPaymentSummary?: ITransactionPaymentSummary | null,
  shows: IShowDogs[],
  isLottery: boolean,
  behalfUserId?: string,
  sanctioningClub?: string,
  showFees?: boolean,
  isFinalStep?: boolean,
  allEntriesWaitlisted?: boolean,
  clubAbsorbsFees?: boolean,
  // Price override props (club manager / on-behalf only)
  feeOverrides?: Record<string, number>,
  originalPrices?: Record<string, number>,
  onFeeOverride?: (key: string, amountDollars: number, originalFee: number) => void,
  onClearOverride?: (key: string) => void,
  overrideNote?: string,
  onOverrideNoteChange?: (note: string) => void,
}

interface ICartDetails extends ITransactionShowDetails {
  expanded: boolean
}

const Cart: React.FC<IProps> = (props) => {
  const [discount, setDiscount] = useState<string>("");
  const [cartDetails, setCartDetails] = useState<ICartDetails[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [localValues, setLocalValues] = useState<Record<string, string>>({});
  const [originalFees, setOriginalFees] = useState<Record<string, number>>({});
  const currentUser = helperService.getUser();

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: props.paymentSummary.currency.toUpperCase() || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  // Check if any shows have waitlists enabled
  /*const hasAnyWaitlistEnabled = () => {
    return props.shows.some(show => show.waitlist_enabled);
  };*/

  const getShowSubTotal = (show: ITransactionShowDetails) => {
    let total:number = 0;

    for (let d in show.dogs) {
      for (let se in show.dogs[d].show_elements) {
        let showElement = show.dogs[d].show_elements[se];

        if (props.allEntriesWaitlisted && previewFeeMap.size > 0) {
          const key = `${show.show_id}|${show.dogs[d].dog_id}|${showElement.show_element}|${showElement.level || ""}`;
          const previewFee = previewFeeMap.get(key);
          if (previewFee) total += previewFee;
        } else if (showElement.fee && (!showElement.waitlist || props.isLottery || currentUser?.user_type === Constants.user_type.club_manager)) {
          total += showElement.fee;
        }
      }
    }

    return total;
  }

  const toggleShowItemDetails = (index: number) => {
    let _cartDetails = JSON.parse(JSON.stringify(cartDetails));
    _cartDetails[index].expanded = !_cartDetails[index].expanded;

    setCartDetails(_cartDetails);
  }

  const enterEditMode = () => {
    const init: Record<string, string> = {};
    const origFees: Record<string, number> = {};
    for (const show of cartDetails) {
      for (const dog of show.dogs) {
        for (const se of dog.show_elements) {
          const key = `${show.show_id}::${dog.dog_id}::${se.show_element}::${se.level}`;
          const overrideVal = props.feeOverrides?.[key];
          init[key] = overrideVal !== undefined ? String(overrideVal) : String(se.fee);
          origFees[key] = se.fee;
        }
      }
    }
    setOriginalFees(origFees);
    setLocalValues(init);
    setIsEditing(true);
  };

  const saveOverrides = () => {
    for (const [key, val] of Object.entries(localValues)) {
      const parsed = parseFloat(val);
      if (isNaN(parsed) || parsed < 0) continue;
      const isChanged = parsed !== originalFees[key];
      if (isChanged && props.onFeeOverride) {
        props.onFeeOverride(key, parsed, originalFees[key]);
      } else if (!isChanged && props.onClearOverride) {
        props.onClearOverride(key);
      }
    }
    setIsEditing(false);
  };

  const cancelEdit = () => {
    setIsEditing(false);
  };

  const pDetails = helperService.usePrevious(props.paymentSummary.details);

  // Total number of class/level entries across all dogs and shows in the cart.
  const totalEntries = useMemo<number>(() => {
    return cartDetails.reduce((showSum, show) => {
      return showSum + (show.dogs || []).reduce((dogSum, dog) => {
        return dogSum + (dog.show_elements?.length || 0);
      }, 0);
    }, 0);
  }, [cartDetails]);

  // When all entries are waitlisted and we have a preview summary, build a lookup
  // of real fees keyed by "show_id|dog_id|show_element|level" so we can show actual
  // prices in the line items rather than CA$0.00.
  const previewFeeMap = useMemo<Map<string, number>>(() => {
    const map = new Map<string, number>();
    if (!props.allEntriesWaitlisted || !props.previewPaymentSummary) return map;
    for (const show of (props.previewPaymentSummary.details || []) as ITransactionShowDetails[]) {
      for (const dog of show.dogs || []) {
        for (const se of dog.show_elements || []) {
          const key = `${show.show_id}|${dog.dog_id}|${se.show_element}|${se.level || ""}`;
          map.set(key, se.fee);
        }
      }
    }
    return map;
  }, [props.allEntriesWaitlisted, props.previewPaymentSummary]);

  useEffect(() => {
    if (pDetails !== props.paymentSummary.details) {
      let details:ITransactionShowDetails[] = JSON.parse(JSON.stringify(props.paymentSummary.details));
      let cart:ICartDetails[] = JSON.parse(JSON.stringify(cartDetails));

      if (details.length === 0) {
        cart = [];
      } else {
        for (let d in details) {
          if (cart[d]) {
            cart[d] = { ...details[d], expanded: cart[d].expanded };
          } else {
            cart.push({ ...details[d], expanded: false });
          }
        }
      }

      setCartDetails(cart);
    }
  }, [pDetails, props.paymentSummary.details, setCartDetails, cartDetails]);

  const getEmptyText = (showType: string) => {
    let text = "No Shows Selected";

    switch(showType) {
      case Constants.dog_class_element.conformation:
        text = "No Classes Selected";
      break;
      case Constants.dog_class_element.performance_obedience:
        text = "No Levels Selected";
      break;
      case Constants.dog_class_element.performance_rally_obedience:
        text = "No Levels Selected";
      break;
      default:
        text = "No Elements Selected";
      break;
    }

    return text;
  }

  const getShowDetailsName = (se: ITransactionElementDetails, showType: string, sanctioningClub?: string) => {
    let baseName: string;
    
    if (se.show_element === CKC_TCN_FEE_LABEL) {
      // CKC: per-show listing fee for a dog entered on a Temporary Competition Number
      baseName = CKC_TCN_FEE_LABEL;
    } else if (showType === Constants.show_type.canine_good_citizen) {
      baseName = [se.show_element, se.level].join(" - ");
    } else if (helperService.timeSlotShows.includes(showType)) {
      baseName = se.level;
    } else if (showType === Constants.show_type.barn_hunt) {
      // For Barn Hunt, only show level (and section for applicable levels)
      // Only Novice, Open, Senior, and Master have sections
      const hasSection = [
        Constants.dog_class_element_level.barn_hunt_novice,
        Constants.dog_class_element_level.barn_hunt_open,
        Constants.dog_class_element_level.barn_hunt_senior,
        Constants.dog_class_element_level.barn_hunt_master
      ].includes(se.level);
      baseName = se.level + (hasSection && se.section && se.section !== "None" ? " " + se.section : "");
    } else if (se.show_element === se.level) {
      baseName = "Non-Licensed Class, " + se.level;
    } else if (sanctioningClub === Constants.sanctioning_club.AKC && 
               (showType === Constants.show_type.obedience || showType === Constants.show_type.rally_obedience)) {
      // For AKC Obedience and Rally: only show level (no element, no section)
      baseName = se.level;
    } else if (sanctioningClub === Constants.sanctioning_club.CKC &&
               [Constants.dog_class_element.conformation, Constants.dog_class_element.performance_obedience, Constants.dog_class_element.performance_rally_obedience].includes(se.show_element)) {
      // For CKC Conformation, Obedience and Rally Obedience: the regular element repeats the show type, so
      // show only the level. Sweepstakes, Non-Regular and Unofficial classes keep their element name.
      baseName = se.level;
    } else if (sanctioningClub === Constants.sanctioning_club.C_WAGS && 
               (showType === Constants.show_type.obedience || showType === Constants.show_type.rally_obedience)) {
      // For C-WAGS Obedience and Rally: only show level and section (no element)
      baseName = se.level + (se.section && se.section !== "None" ? ", " + se.section : "");
    } else if (sanctioningClub === Constants.sanctioning_club.FH && showType === Constants.show_type.rally_obedience) {
      // For 4-H Rally Obedience: only show level (no element)
      baseName = se.level;
    } else if (sanctioningClub === Constants.sanctioning_club.ABI && showType === Constants.show_type.scent_dog_games) {
      // For ABI Scent Dog Games: show only the game name (level) and section — element is always "Scent Dog Games"
      baseName = se.level + (se.section && se.section !== "None" ? " - " + se.section : "");
    } else if (sanctioningClub === Constants.sanctioning_club.ABI && showType === Constants.show_type.lost_and_found) {
      // For ABI Lost and Found: show element, level, and on/off leash section
      baseName = se.show_element + ", " + se.level + (se.section && se.section !== "None" ? " - " + se.section : "");
    } else {
      let showElementName = se.show_element;
      if (showType === Constants.show_type.country_rat_race) {
        showElementName = "CRR";
      }
      
      // Show types that should display section (from showTypeUsesSections helper)
      const showTypesWithSection = [
        Constants.show_type.nosework,  // UKC Nosework
        Constants.show_type.herding,   // AKC Herding
        Constants.show_type.country_rat_race  // ABI Country Rat Race
      ];
      
      // Add C-WAGS shows that use sections
      const isCWagsWithSections = sanctioningClub === Constants.sanctioning_club.C_WAGS && 
        (showType === Constants.show_type.obedience || showType === Constants.show_type.rally_obedience);
      
      // C-WAGS Scent / Games FEO runs: "Scent, Patrol 1 FEO"
      const isCWagsFeoRun = sanctioningClub === Constants.sanctioning_club.C_WAGS &&
        se.section === Constants.dog_class_section.feo;

      baseName = showElementName +", "+ se.level +
        (isCWagsFeoRun ? " " + se.section :
          (!showTypesWithSection.includes(showType) && !isCWagsWithSections ? "" : se.section !== "None" && se.section !== se.level ? ", " + se.section : ""));
    }

    // Add waitlist position if present (suppress for random draw — position is not meaningful)
    if (se.waitlist && se.waitlist_position && !props.isLottery) {
      baseName += ` (${se.waitlist_position})`;
    }

    if (se.is_junior) {
      baseName += ` (junior)`;
    }

    return baseName;
  }

  const getShowElementRegularFee = (showElement: ITransactionElementDetails, show?: IShow) => {
    if (show?.use_same_fee && show?.fee) {
      return show.fee;
    }

    return showElement.regular_fee;
  }

  const quotedSummary = props.allEntriesWaitlisted && props.previewPaymentSummary
    ? props.previewPaymentSummary
    : props.paymentSummary;
  const subtotalAmount = props.clubAbsorbsFees ? quotedSummary.total_amount : quotedSummary.base_amount;
  const totalLines: ICartTotalLine[] = [
    { label: "Subtotal", value: formatter.format(subtotalAmount || 0) },
  ];
  if (props.showFees && !props.clubAbsorbsFees) {
    if (props.allEntriesWaitlisted && !props.previewPaymentSummary) {
      totalLines.push({ label: "Platform Fees", value: "Applied at charge", pending: true });
      totalLines.push({ label: "Processing Fees", value: "Applied at charge", pending: true });
    } else {
      totalLines.push({ label: "Platform Fees", value: formatter.format((quotedSummary.calculated_application_fee / 100) || 0) });
      totalLines.push({
        label: "Processing Fees",
        value: formatter.format(((quotedSummary.calculated_processing_fee / 100) + (quotedSummary.calculated_processing_tax / 100)) || 0),
      });
    }
  }
  if ((props.paymentSummary?.discount || 0) > 0) {
    totalLines.push({ label: "Discount", value: `-${formatter.format(props.paymentSummary.discount)}` });
  }
  if ((props.paymentSummary?.club_points_discount_amount || 0) > 0) {
    totalLines.push({
      label: <>Points Redeemed (<strong>{(props.paymentSummary.club_points_redeemed || 0).toLocaleString()}</strong>)</>,
    });
  }
  const totalLabel = props.allEntriesWaitlisted ? "Due Today" : (props.isFinalStep ? "Total" : "Subtotal");
  const totalValue = props.allEntriesWaitlisted
    ? formatter.format(0)
    : formatter.format(props.showFees
      ? (props.paymentSummary?.total_amount || 0)
      : (props.clubAbsorbsFees ? props.paymentSummary.total_amount : props.paymentSummary.base_amount) || 0);

  return (
    <div className="Cart">
      <div className="cartInner">
        <div className="title">
          <span>Your Cart{totalEntries > 0 && <span className="entryCount"> ({totalEntries} {totalEntries === 1 ? "entry" : "entries"})</span>}</span>
          {props.onFeeOverride && (
            <div className={`cartEditBtn${isEditing ? " active" : ""}`} onClick={isEditing ? saveOverrides : enterEditMode} title={isEditing ? "Save prices" : "Override prices"}>
              <button className="icon edit" />
            </div>
          )}
        </div>
        {/*{hasAnyWaitlistEnabled() && (*/}
        {/*  <div className="waitlistInfo"><b>Note:</b> If your entry has a number in brackets after it, that indicates your current estimated waitlist position.*/}
        {/*  Waitlist entries must be paid for at the time of checkout. If your entry does not make it into the event, your payment will be refunded minus non-refundable processing fees charged by Stripe and Eager Dog.</div>*/}
        {/*)}*/}
        <div className="items">
          {cartDetails.length > 0 && <div className="cartSubTitle">Shows</div>}
          <div className="showItemWrap">
            {cartDetails.map((show: ICartDetails, showIndex: number) => {
              let showFull:IShow[] = props.shows.filter((s: IShow) => { return s._id === show.show_id; });
              return(
                <div key={showIndex} className="showItem">
                  <div className="showTitle">
                    <div className="showName">
                      <div className="showNameInner">{show.custom_show_name || `${show.show_type} ${show.show_name}`}</div>
                      <span className="showTotal">{showFull.length > 0 && <>{moment(showFull[0].show_date).format("MMM Do")} - </>}{show.dogs.length > 0 ? formatter.format(getShowSubTotal(show)) + " ("+ show.dogs.length +" "+ (show.dogs.length > 1 ? "dogs" : "dog") +")" : getEmptyText(show.show_type)}</span>
                    </div>
                    {<div className="showActions"><div onClick={(e) => { toggleShowItemDetails(showIndex); }} className={"icon " + (show.expanded ? "caretDown" : "caretUp")}></div></div>}
                  </div>
                  {show.expanded && <div className="showDetails no-select">
                    {show.dogs.map((dog: ITransactionDogDetails, dIndex:number) => {
                      return(<div key={dIndex} className="dogList">
                        <div className="dogName">{dog.call_name}</div>
                        {dog.show_elements.length === 0 && <span className="empty">{getEmptyText(show.show_type)}</span>}
                        {dog.show_elements.length > 0 && dog.show_elements.map((se: ITransactionElementDetails, elementIndex:number) => {
                          const elementFee = getShowElementRegularFee(se, showFull[0]);
                          const overrideKey = `${show.show_id}::${dog.dog_id}::${se.show_element}::${se.level}`;
                          const overriddenAmount = props.feeOverrides?.[overrideKey];

                          return(<div key={"dogClass" + elementIndex} className={`dogClass${isEditing ? " editingMode" : ""}`}>
                            <span className={se.waitlist ? "waitlist" : ""}>{getShowDetailsName(se, show.show_type, props.sanctioningClub)}</span>
                            <span className="priceDisplay">
                              {isEditing ? (
                                <span className="overrideInputWrap">
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={localValues[overrideKey] ?? String(se.fee)}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                      setLocalValues(prev => ({ ...prev, [overrideKey]: e.target.value }))
                                    }
                                  />
                                  {overriddenAmount === undefined && (
                                    <span className="originalFeeHint">{formatter.format(se.fee)}</span>
                                  )}
                                </span>
                              ) : overriddenAmount !== undefined ? (
                                <span className="overrideDisplay">
                                  <span className="overrideAmount">{formatter.format(overriddenAmount)}</span>
                                  <span className="overrideBadge">override</span>
                                  <span className="originalStrike">{formatter.format(props.originalPrices?.[overrideKey] ?? originalFees[overrideKey] ?? se.fee)}</span>
                                </span>
                              ) : (
                                <>
                                  {se.is_early_bird && elementFee !== undefined && elementFee !== se.fee ? (
                                    <>
                                      <span className="regularPrice">{formatter.format(elementFee)}</span>
                                      <span className="earlyBirdPrice">{formatter.format(se.fee)}</span>
                                    </>
                                  ) : (
                                    <span>{se.waitlist && !props.isLottery && !props.allEntriesWaitlisted ? "Waitlisted" : formatter.format(previewFeeMap.get(`${show.show_id}|${dog.dog_id}|${se.show_element}|${se.level || ""}`) ?? se.fee)}</span>
                                  )}
                                </>
                              )}
                            </span>
                          </div>);
                        })}
                      </div>);
                    })}
                  </div>}
                </div>
              );
            })}
          </div>
          {props.paymentSummary.add_ons_details !== undefined && props.paymentSummary.add_ons_details.length > 0 && <div className="cartSubTitle">Add-ons</div>}
          {props.paymentSummary.add_ons_details !== undefined && props.paymentSummary.add_ons_details.length > 0 && <div className="addOnNote"><b>Note:</b> Add-on purchases aren't included in the $10 cap for entries. If you include add-ons, your Platform Fees may be higher than $10</div>}
          <div className="showItemWrap">
            {props.paymentSummary.add_ons_details && props.paymentSummary.add_ons_details.length > 0 && props.paymentSummary.add_ons_details.map((addon, index) => (
              <div key={index} className="showItem">
                <div className="showTitle">
                  <div className="showName">
                    <span className="showNameInner">{addon.add_on_title}</span>
                    <span className="showTotal">{addon.quantity} x {formatter.format(addon.fee)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {props.paymentSummary.details.length === 0 && <span className="empty">You haven't selected a class/element/level for your dogs shows/trials yet.</span>}
        </div>
        <>{props.onApplyDiscount && <form className="discountCode">
          <Input required label="Discount Code" id="discountCode" type="text" onChange={(e) => { e.stopPropagation(); let _code = e.target.value.toUpperCase().replace(/\s/g, '').replace(/[^a-zA-Z0-9 ]/g, ""); setDiscount(_code); }} defaultValue={discount} />
          <button type="submit" onClick={(e: any) => { e.preventDefault(); if (props.onApplyDiscount) { props.onApplyDiscount(discount.toUpperCase()); } }}>Apply</button>
        </form>}</>
        
        {props.onRedeemPoints && (
          <PointRedemption
            clubId={props.clubId}
            behalfUserId={props.behalfUserId}
            redeemableAmount={props.paymentSummary.base_amount || 0}
            onRedeem={props.onRedeemPoints}
          />
        )}
        
        {isEditing && (
          <div className="editModeActions" style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", alignItems: "center", gap: "8px", padding: "10px 0", borderTop: "1px solid #e0e0e0", marginTop: "10px" }}>
            <Button onClick={cancelEdit} variant="secondary" style={{ width: "auto", flex: "0 0 auto" }}>Cancel</Button>
            <Button onClick={saveOverrides} style={{ width: "auto", flex: "0 0 auto" }}>Save Prices</Button>
          </div>
        )}

        {props.onOverrideNoteChange && props.feeOverrides && Object.keys(props.feeOverrides).length > 0 && (
          <div className="overrideNote">
            <label htmlFor="overrideNoteInput" className="overrideNoteLabel">Override Note (optional)</label>
            <textarea
              id="overrideNoteInput"
              className="overrideNoteInput"
              value={props.overrideNote || ""}
              onChange={(e) => props.onOverrideNoteChange!(e.target.value)}
              placeholder="Reason for price override..."
              rows={2}
            />
          </div>
        )}
        <CartTotals
          lines={totalLines}
          totalLabel={totalLabel}
          totalValue={totalValue}
          note={props.isLottery ? "Est. total (Assuming all entries are approved)" : undefined}
        />
      </div>
    </div>
  );
};

export default Cart;